// SessionNotesRepair.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  useToast,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Progress,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import { RepeatIcon, CheckIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';
import supabase from '../supabaseClient';
import jsPDF from 'jspdf';

const SessionNotesRepair = () => {
  const [orphanedNotes, setOrphanedNotes] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [repairProgress, setRepairProgress] = useState({});
  const [scanResults, setScanResults] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Scan for orphaned session notes
  const scanForOrphanedNotes = async () => {
    setIsScanning(true);
    try {
      // Simple SQL query to find session notes without client documents
      const { data: orphanedData, error } = await supabase.rpc('find_orphaned_session_notes');
      
      if (error) {
        // Fallback to manual check if RPC doesn't exist
        console.log('RPC not found, using manual check...');
        
        // Get session notes that don't have matching client_documents
        const { data: sessionNotes, error: notesError } = await supabase
          .from('session_notes')
          .select(`
            id,
            client_id,
            session_date,
            created_at,
            clients!inner(name, nickname),
            therapists!inner(name, specialization)
          `)
          .order('session_date', { ascending: false });

        if (notesError) throw notesError;

        console.log(`Found ${sessionNotes.length} total session notes`);

        // Get all session note document IDs to check against
        const { data: docNotes, error: docsError } = await supabase
          .from('client_documents')
          .select('client_id, title, created_at')
          .eq('document_category', 'Session Notes');

        if (docsError) throw docsError;

        console.log(`Found ${docNotes.length} total session note documents`);

        // Find session notes that don't have any corresponding document
        const orphaned = [];
        
        for (const note of sessionNotes) {
          // Check if there's any document for this client around this date
          const sessionDate = new Date(note.session_date);
          const hasDocument = docNotes.some(doc => {
            if (doc.client_id !== note.client_id) return false;
            
            // Check if document was created within a reasonable time of the session
            const docDate = new Date(doc.created_at);
            const daysDiff = Math.abs((docDate - sessionDate) / (1000 * 60 * 60 * 24));
            
            return daysDiff <= 30; // Document created within 30 days of session
          });

          if (!hasDocument) {
            orphaned.push({
              ...note,
              clientName: note.clients.name,
              clientNickname: note.clients.nickname,
              therapistName: note.therapists.name,
              specialization: note.therapists.specialization
            });
          }
        }

        setOrphanedNotes(orphaned);
        setScanResults({
          totalNotes: sessionNotes.length,
          totalDocs: docNotes.length,
          orphanedCount: orphaned.length,
          completedAt: new Date()
        });

        console.log(`Found ${orphaned.length} session notes without documents:`);
        orphaned.forEach(note => {
          console.log(`- ID: ${note.id}, Client: ${note.clientName}, Date: ${note.session_date}`);
        });
      }

    } catch (error) {
      console.error('Error scanning for orphaned notes:', error);
      toast({
        title: 'Scan failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsScanning(false);
    }
  };

  // Get specialization abbreviation
  const getSpecializationAbbrev = (specialization) => {
    switch (specialization) {
      case 'Occupational Therapy':
        return 'OT';
      case 'Speech Therapy':
        return 'ST';
      case 'Physical Therapy':
        return 'PT';
      case 'Play Group':
        return 'PG';
      default:
        return 'OT';
    }
  };

  // Get specialization designation
  const getSpecializationDesignation = (specialization) => {
    switch (specialization) {
      case 'Occupational Therapy':
        return 'OTRP';
      case 'Speech Therapy':
        return 'RSLP';
      case 'Physical Therapy':
        return 'PTRP';
      case 'Play Group':
        return 'PGF';
      default:
        return 'OTRP';
    }
  };

  // Helper function to load image as base64
  const loadImageAsBase64 = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      };
      img.onerror = () => resolve(null); // Return null if logo fails to load
      img.src = src;
    });
  };

  // Generate PDF for a session note
  const generatePDFForNote = async (note) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;
      let yPosition = margin;
      
      const colors = {
        primary: [36, 181, 232],
        yellow: [255, 172, 17],
        green: [171, 219, 21],
        purple: [220, 66, 145],
        lightYellow: [255, 248, 220],
        lightGreen: [240, 251, 207],
        lightBlue: [224, 247, 255],
        lightPurple: [252, 240, 249],
        white: [255, 255, 255],
        gray: [128, 128, 128],
        black: [0, 0, 0],
        darkGray: [64, 64, 64],
        lightGray: [248, 249, 250]
      };
      
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 9, fontWeight = 'normal', color = colors.darkGray, maxWidth = null, lineHeight = 1.3 } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontWeight);
        doc.setTextColor(...color);
        
        if (maxWidth) {
          const lines = doc.splitTextToSize(text, maxWidth);
          lines.forEach((line, index) => {
            doc.text(line, x, y + (index * fontSize * lineHeight));
          });
          return y + (lines.length * fontSize * lineHeight);
        } else {
          doc.text(text, x, y);
          return y + (fontSize * lineHeight);
        }
      };

      // HEADER - Try to load logo, fallback to text
      try {
        const logoBase64 = await loadImageAsBase64('/tt-logo.png');
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', 3, 0, 75, 25);
        } else {
          throw new Error('Logo not available');
        }
      } catch (logoError) {
        // Fallback to text logo
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.primary);
        doc.text('TT', 8, 14);
      }

      // Company info
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.black);
      doc.text('3rd Floor Allegro Center, Pasong Tamo Ext., Makati', pageWidth - 12, 17, { align: 'right' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.gray);
      doc.text('+639171600036 | tinytriumphsph@gmail.com', pageWidth - 12, 21, { align: 'right' });

      doc.setFillColor(...colors.primary);
      doc.rect(12, 23, pageWidth - 23, 2, 'F');
      
      yPosition = 28;

      // CLIENT INFO
      let clientInfo = note.clientName;
      if (note.clientNickname) clientInfo += ` "${note.clientNickname}"`;
      const dateInfo = new Date(note.session_date).toLocaleDateString();
      
      const clientInfoY = yPosition;
      
      // Client Name section
      doc.setFillColor(...colors.purple);
      doc.roundedRect(margin + 2, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Client Name:', margin + 4, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      const clientNameWidth = Math.max(60, doc.getTextWidth(clientInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'S');
      addText(clientInfo, margin + 29, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      // Date section
      const dateStartX = pageWidth - 55;
      doc.setFillColor(...colors.purple);
      doc.roundedRect(dateStartX, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Date:', dateStartX + 2, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      const dateWidth = Math.max(25, doc.getTextWidth(dateInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'S');
      addText(dateInfo, dateStartX + 19, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      yPosition += 15;

      // Add basic content sections
      addText('SUBJECTIVE', margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
      yPosition += 8;
      
      // Present with
      const presentWith = note.present_with?.join(', ') || 'Not specified';
      addText(`Present with: ${presentWith}`, margin + 4, yPosition, { fontSize: 10 });
      yPosition += 6;
      
      // Child behavior
      const behavior = note.child_behavior?.join(', ') || 'Not specified';
      addText(`Child behavior: ${behavior}`, margin + 4, yPosition, { fontSize: 10, maxWidth: pageWidth - 24 });
      yPosition += 15;

      // Activities section
      addText('ACTIVITIES & PERFORMANCE', margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
      yPosition += 8;
      
      if (note.activities) {
        Object.entries(note.activities).forEach(([key, activity], index) => {
          addText(`Activity ${index + 1}: ${activity.name || 'No description'}`, margin + 4, yPosition, { fontSize: 10 });
          yPosition += 6;
          if (activity.performance_notes) {
            addText(`Notes: ${activity.performance_notes}`, margin + 8, yPosition, { fontSize: 9, maxWidth: pageWidth - 24 });
            yPosition += 6;
          }
          yPosition += 4;
        });
      }

      // Progress section
      yPosition += 10;
      addText('OVERALL PROGRESS', margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
      yPosition += 8;
      addText(note.overall_progress || 'Not specified', margin + 4, yPosition, { fontSize: 10 });
      yPosition += 15;

      // Therapist signature
      addText('Prepared by:', margin, yPosition, { fontSize: 10, fontWeight: 'bold' });
      yPosition += 6;
      const designation = getSpecializationDesignation(note.specialization);
      addText(`${note.therapistName}, ${designation}`, margin, yPosition, { fontSize: 10 });
      yPosition += 4;
      addText('Therapist In-Charge', margin, yPosition, { fontSize: 8, color: colors.gray });

      // Generate filename
      const clientName = note.clientName.replace(/\s+/g, '_');
      const sessionDate = new Date(note.session_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const abbrev = getSpecializationAbbrev(note.specialization);
      const filename = `${abbrev}_Session_Note_${clientName}_${sessionDate}.pdf`;
      
      return {
        pdfBlob: doc.output('blob'),
        filename
      };

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  };

  // Repair a single session note
  const repairSingleNote = async (note) => {
    const noteId = note.id;
    setRepairProgress(prev => ({ ...prev, [noteId]: 'generating' }));

    try {
      // Generate PDF
      const { pdfBlob, filename } = await generatePDFForNote(note);
      
      setRepairProgress(prev => ({ ...prev, [noteId]: 'uploading' }));

      // Upload to storage
      const fileExt = 'pdf';
      const storageFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${note.client_id}/session_notes/${storageFileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('client-documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf'
        });

      if (uploadError) throw uploadError;

      setRepairProgress(prev => ({ ...prev, [noteId]: 'saving' }));

      // Save document record
      const documentData = {
        client_id: note.client_id,
        document_category: 'Session Notes',
        document_type: 'Session Note',
        title: note.expectedTitle,
        description: `Session note by ${note.therapistName} on ${new Date(note.session_date).toLocaleDateString()}`,
        file_name: filename,
        file_path: filePath,
        file_size: pdfBlob.size,
        mime_type: 'application/pdf'
      };

      const { error: insertError } = await supabase
        .from('client_documents')
        .insert([documentData]);

      if (insertError) throw insertError;

      setRepairProgress(prev => ({ ...prev, [noteId]: 'completed' }));

      // Remove from orphaned list
      setOrphanedNotes(prev => prev.filter(n => n.id !== noteId));

      toast({
        title: 'Repair successful',
        description: `PDF document created for ${note.clientName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error repairing note:', error);
      setRepairProgress(prev => ({ ...prev, [noteId]: 'failed' }));
      
      toast({
        title: 'Repair failed',
        description: `Failed to repair session note: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // View note details
  const viewNoteDetails = (note) => {
    setSelectedNote(note);
    onOpen();
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'generating': return 'blue';
      case 'uploading': return 'orange';
      case 'saving': return 'purple';
      case 'completed': return 'green';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getProgressText = (status) => {
    switch (status) {
      case 'generating': return 'Generating PDF...';
      case 'uploading': return 'Uploading file...';
      case 'saving': return 'Saving record...';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return 'Ready';
    }
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Card>
          <CardHeader>
            <Heading size="lg" color="#24b5e8">Session Notes Repair Tool</Heading>
            <Text color="gray.600" mt={2}>
              Identify and repair session notes that are missing from the client documents table.
            </Text>
          </CardHeader>
        </Card>

        {/* Scan Section */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Heading size="md">Step 1: Scan for Issues</Heading>
                <Button
                  leftIcon={<RepeatIcon />}
                  colorScheme="blue"
                  onClick={scanForOrphanedNotes}
                  isLoading={isScanning}
                  loadingText="Scanning..."
                >
                  Scan Session Notes
                </Button>
              </HStack>

              {scanResults && (
                <Alert status={orphanedNotes.length > 0 ? 'warning' : 'success'}>
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <AlertTitle>Scan Results</AlertTitle>
                    <AlertDescription>
                      Found {orphanedNotes.length} session notes without corresponding documents out of {scanResults.totalNotes} total notes.
                      <br />
                      <Text fontSize="sm" color="gray.600">
                        Scanned at: {scanResults.completedAt.toLocaleString()}
                      </Text>
                    </AlertDescription>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Results Section */}
        {orphanedNotes.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Step 2: Repair Orphaned Notes</Heading>
              <Text color="gray.600" mt={2}>
                The following session notes don't have corresponding records in client_documents:
              </Text>
            </CardHeader>
            <CardBody>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Session Note ID</Th>
                    <Th>Client</Th>
                    <Th>Session Date</Th>
                    <Th>Therapist</Th>
                    <Th>Specialization</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orphanedNotes.map((note) => (
                    <Tr key={note.id}>
                      <Td>
                        <Badge colorScheme="gray" fontFamily="mono">
                          {note.id}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">{note.clientName}</Text>
                          {note.clientNickname && (
                            <Text fontSize="sm" color="gray.500">"{note.clientNickname}"</Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>{new Date(note.session_date).toLocaleDateString()}</Td>
                      <Td>{note.therapistName}</Td>
                      <Td>
                        <Badge colorScheme="blue">
                          {getSpecializationAbbrev(note.specialization)}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="View note details">
                            <IconButton
                              icon={<InfoIcon />}
                              size="sm"
                              variant="outline"
                              onClick={() => viewNoteDetails(note)}
                            />
                          </Tooltip>
                          <Button
                            leftIcon={<CheckIcon />}
                            size="sm"
                            colorScheme="green"
                            onClick={() => repairSingleNote(note)}
                            isDisabled={repairProgress[note.id] && repairProgress[note.id] !== 'failed'}
                            isLoading={repairProgress[note.id] && ['generating', 'uploading', 'saving'].includes(repairProgress[note.id])}
                          >
                            Repair
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        )}

        {orphanedNotes.length === 0 && scanResults && (
          <Alert status="success">
            <AlertIcon />
            <AlertTitle>All Good!</AlertTitle>
            <AlertDescription>
              No orphaned session notes found. All session notes have their corresponding PDF documents.
            </AlertDescription>
          </Alert>
        )}
      </VStack>

      {/* Note Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Session Note Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedNote && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold">Client:</Text>
                  <Text>{selectedNote.clientName} {selectedNote.clientNickname && `"${selectedNote.clientNickname}"`}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Session Date:</Text>
                  <Text>{new Date(selectedNote.session_date).toLocaleDateString()}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Therapist:</Text>
                  <Text>{selectedNote.therapistName} ({selectedNote.specialization})</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Expected Document Title:</Text>
                  <Text fontSize="sm" bg="gray.100" p={2} borderRadius="md">{selectedNote.expectedTitle}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Present With:</Text>
                  <Text>{selectedNote.present_with?.join(', ') || 'Not specified'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Overall Progress:</Text>
                  <Text>{selectedNote.overall_progress || 'Not specified'}</Text>
                </Box>
                {selectedNote.key_notes && (
                  <Box>
                    <Text fontWeight="bold">Key Notes:</Text>
                    <Text fontSize="sm">{selectedNote.key_notes}</Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SessionNotesRepair;